import { useEffect, useState } from 'react';
import { LINK_LEAD, LINK_LEAD_STAYS, LINK_PENDING, PAY_ASK, lineTone, moneyLines, moneyNotRightLine, shapeMessage, stayDay, withLinkLine, type MoneyLine, type PaymentProof } from '@bbbk/schema';
import { Icon } from '../common';
import { MONTHS_ABBR } from '../../lib/dates';
import type { BookingDetail } from './types';
import { ProofFull } from './ProofImage';
import { dates, dogNames } from './MessageSlot';
import { DecideBlock, DecideSkeleton, DropZone, SelfServiceCard, SelfServiceCardNa, Shot, TrailRow, TrailThumb, type Band, type BandTone, type Pill, type TrailClass } from './SelfServiceCard';

// THE MONEY CARD — THE PROTOTYPE'S OWN (G28; Freddi, 2026-10-11: "the money
// card does not match the prototype at all, line that up").
//
// A band is the head: the title, one sentence saying where the money stands,
// the figure. It starts shut (ruled 2026-10-11) and opens to the trail — one
// row per line, newest first, each with its thumbnail, its words and its
// pill: Done, To check, Not right, Due. A row opens to the shot beside what
// the state needs: the amount and Accept · Not right while a screenshot waits
// on Patricia (AC9, AC10a); the line she sends with Copy message and Open
// WhatsApp after a no (AC11); the record once it is paid; her own drop zone
// and a message to send while a line is still due (AC20).
//
// The rules are `moneyLines` in @bbbk/schema, the same ones the API decides
// with, so this card can never say Done while the sheet still asks. Every
// screenshot stays reachable in every state (ruled 2026-10-11): the row's
// thumbnail opens it full screen, and so does the shot.

type CardSubject = { source?: string | null; status: string; proofs?: readonly unknown[] | null; total?: number | string | null };

export function moneyCardShown(b: CardSubject): boolean {
  // Any booking at unconfirmed or later, whoever made it. A request draws none (the request
  // card leads, and the stay card says *if accepted*); a decline draws none;
  // a cancelled booking keeps it read-only as its ledger left it (AC9b).
  return notApplicable(b) === null;
}

const euro = (n: number) => (Number.isInteger(n) ? `€${n}` : `€${n.toFixed(2)}`);
// DATES ARE DUBLIN'S (PR #255 review, #34). A timestamp shortly after local
// midnight in summer is still yesterday in UTC, and the accept route dates its
// payment in Europe/Dublin — so the proof and its payment could name two days.
// A calendar date ('2026-10-11') is a day already and is read as written.
const DUBLIN = new Intl.DateTimeFormat('en-IE', { timeZone: 'Europe/Dublin', day: 'numeric', month: 'numeric' });
export function dublinDay(iso: string): string {
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (plain) return `${Number(plain[3])} ${MONTHS_ABBR[Number(plain[2]) - 1]}`;
  const parts = DUBLIN.formatToParts(new Date(iso));
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return `${n('day')} ${MONTHS_ABBR[n('month') - 1]}`;
}
const day = dublinDay;

export interface MoneyState {
  lines: { deposit: MoneyLine; remainder: MoneyLine };
  lead: MoneyLine;
  settled: boolean;
  tone: ReturnType<typeof lineTone>;
  total: number;
}

export function moneyState(b: BookingDetail): MoneyState {
  const total = Number(b.total) || 0;
  const lines = moneyLines({
    total,
    depositAsked: b.depositAsked ?? 0,
    payments: (b.payments ?? []).map((p) => ({ kind: p.kind, amount: p.amount, voidedAt: p.voidedAt })),
    proofs: b.proofs ?? [],
  });
  const settled = lines.deposit.state === 'paid' && lines.remainder.state === 'paid';
  // A screenshot waiting on Patricia leads, whichever line it is for (PR #255
  // review, #21/#60): a balance proof stays the move even when a cleared
  // deposit payment reopens the deposit line under it.
  const lead = lines.remainder.state === 'pending' && lines.deposit.state !== 'pending'
    ? lines.remainder
    : lines.deposit.state !== 'paid' ? lines.deposit : lines.remainder;
  return { lines, lead, settled, tone: settled ? 'green' : lineTone(lead.state), total };
}

export type { Band, BandTone };

/** The band: where the money stands, in the prototype's own words, and the figure that matters now. */
export function moneyBand(s: MoneyState, b: BookingDetail, first: string): Band {
  if (s.settled) return { tone: 'ok', sentence: 'Paid in full', figure: euro(s.total), small: 'paid' };
  // The figure is what is still outstanding on the whole stay (the drawing's
  // "€240 due"), whichever line the sentence is about.
  const { lead } = s;
  const owed = euro(s.lines.remainder.left);
  if (lead.state === 'pending') return { tone: 'warn', sentence: 'Screenshot uploaded · check it', figure: owed, small: 'due' };
  if (lead.state === 'refused') return { tone: 'due', sentence: `Screenshot not right · ${first} asked again`, figure: owed, small: 'due' };
  if (lead.kind === 'remainder') return { tone: 'due', sentence: 'Balance due', figure: owed, small: `due · ${day(b.checkOut)}` };
  if (lead.paid > 0) return { tone: 'due', sentence: `${euro(lead.left)} of the deposit still to come`, figure: owed, small: 'due' };
  return { tone: 'none', sentence: 'Nothing paid', figure: owed, small: 'due' };
}

/** The band's sentence alone — what a glance reads. */
export function moneyGlance(s: MoneyState, b: BookingDetail, first: string): string {
  return moneyBand(s, b, first).sentence;
}

export interface MoneyCardProps {
  booking: BookingDetail;
  /** The customer's first name — the band and the lines to send name them. */
  first: string;
  /** The first name of the household's person with this sign-in, or null — who sent a screenshot. */
  nameOf?: (email: string) => string | null;
  /** Whether a sign-in is the recipient's own — so a screenshot is "your" only when it is (PR #275 review). */
  sentByRecipient?: (email: string) => boolean;
  today: string;
  busy: boolean;
  /** The number of the person the message is for, for Open WhatsApp. */
  phone?: string | null;
  /**
   * The sign-up link for a person with no account (messages-one-shape AC2):
   * their live link, `LINK_PENDING` when one is still to be made, or null.
   */
  link?: string | null;
  /** Makes the link when Patricia taps WhatsApp, and hands it back (AC2a). */
  issueLink?: () => Promise<string | null>;
  onAccept: (proofId: string, amount: number) => void;
  onNotRight: (proofId: string) => void;
  /** Patricia's own screenshot; `kind` names a line she already recorded — never too late for one (Freddi, 2026-10-13). */
  onUpload: (file: File, kind?: 'deposit' | 'remainder') => void;
  /** Remove a screenshot — the row and its object; a payment it recorded stays (Freddi, 2026-10-11). */
  onRemove: (proofId: string) => void;
  /** Test seams: every row open; the remove confirm already showing for that proof. */
  rowsOpen?: boolean;
  confirmRemove?: string | null;
  /** The booking re-reading after a verdict — the decision shows as a skeleton till it lands (Freddi, 2026-10-14). */
  settling?: boolean;
  /** Test seam: the verdict pressed. */
  pendingVerdict?: { proofId: string; verdict: Verdict } | null;
}

type Verdict = 'accept' | 'reject';


/** The greyed card: the band alone, Not applicable and why. */
export function MoneyCardNa({ why }: { why: string }) {
  return <SelfServiceCardNa subject="money" kind="money ss-money" title="Money" why={why} />;
}

/** Why there is no money to show yet — the greyed card's line (Freddi, 2026-10-11). */
export function notApplicable(b: CardSubject): string | null {
  // WHOEVER MADE THE BOOKING (Freddi, 2026-10-12: "the moneys section on the
  // bookings modal should still be open for deposit proof for both patricia and
  // the customer … so it doesnt matter who creates the booking"). A desk booking
  // is live like an app one; only a request or a decline has nothing to show.
  if (b.status === 'requested') return 'Once the request is accepted';
  if (b.status === 'declined') return 'The request was declined';
  // NO PRICE IS NOT PAID (PR #256 review): an old desk booking with no total and an empty ledger
  // read "Paid in full" once the card went live on desk bookings. Until it has a price there is
  // nothing to take money against. `undefined` is a caller that did not say — not no price.
  if (b.total !== undefined && !(Number(b.total) > 0)) return 'No price on this booking yet';
  return null;
}

/**
 * ON EVERY BOOKING (Freddi, 2026-10-11: "the moneys card should always be
 * visible even when a customer's booking has not been accepted it should just
 * be disabled as not applicable so the loading states then can always be the
 * same"). Where there is no money to show, the card is its band alone, greyed:
 * Not applicable and why — no figure, no slot, nothing opens.
 */
export function MoneyCard({ defaultOpen = false, ...props }: MoneyCardProps & { status?: string; defaultOpen?: boolean }) {
  // Shut first, like its sibling (Freddi, 2026-10-11): the band carries the state and the figure —
  // unless a bubble opened the booking for this card (Freddi, 2026-10-14), then it starts unfolded.
  const [open, setOpen] = useState(defaultOpen);
  // ONE COMPONENT, ONE ROOT, BEFORE AND AFTER (2026-10-11): a request accepted turns this card
  // from Not applicable to live in place, so it brightens rather than being swapped out.
  const na = notApplicable({ source: props.booking.source, status: props.status ?? props.booking.status, proofs: props.booking.proofs, total: props.booking.total });
  return <MoneyCardView {...props} na={na} open={open && !na} onToggle={() => setOpen((o) => !o)} />;
}

interface Row {
  line: MoneyLine;
  cls: TrailClass;
  title: string;
  sub: string;
  pill: Pill;
  proof: PaymentProof | null;
  /** The lead row — the line whose move it is — starts open, as the drawing has it; a Done row starts shut. */
  openByDefault: boolean;
}

/**
 * ONE PAYMENT, ONE ROW (Freddi, 2026-10-14: "if only a single payment is received there is only one
 * payment row in the rail … it shows a blank second row and marks as paid"). The stay paid in one go —
 * one live payment covering everything, and no screenshot of the balance's own — is one row, *Paid in
 * full*; so is a stay at or under the minimum, whose deposit is the whole amount and whose balance is
 * €0 and never existed. Anything else keeps the two lines, balance above deposit once it is in play.
 */
type PaymentRow = NonNullable<BookingDetail['payments']>[number];
export function trailLines(s: MoneyState, b: BookingDetail): { lines: MoneyLine[]; whole: { payment: PaymentRow | null; proof: PaymentProof | null } | null } {
  const live = (b.payments ?? []).filter((p) => !p.voidedAt);
  const balanceProof = (b.proofs ?? []).some((p) => p.kind === 'remainder');
  const onePayment = s.settled && live.length === 1 && !balanceProof;
  if (onePayment || (s.lines.remainder.asked <= 0 && s.lines.remainder.state === 'paid' && !balanceProof)) {
    const payment = live.length === 1 ? live[0]! : null;
    const proof = s.lines.deposit.proof ?? (payment?.proofId ? (b.proofs ?? []).find((p) => p.id === payment.proofId) ?? null : null);
    return { lines: [s.lines.deposit], whole: s.settled ? { payment, proof } : null };
  }
  const balanceInPlay = s.lines.deposit.state === 'paid' || s.lines.remainder.state === 'pending' || s.lines.remainder.state === 'refused';
  return { lines: balanceInPlay ? [s.lines.remainder, s.lines.deposit] : [s.lines.deposit], whole: null };
}

function rowOf(line: MoneyLine, b: BookingDetail, first: string, isLead: boolean, nameOf?: (email: string) => string | null, whole: ReturnType<typeof trailLines>['whole'] = null): Row {
  if (whole && line.state === 'paid') {
    // The whole stay, in the one payment that paid it — by its screenshot, or at the desk.
    const pay = whole.payment;
    const sub = pay ? (pay.proofId ? `Paid · ${day(pay.receivedOn)}` : `Paid at the desk · ${day(pay.receivedOn)}`) : 'Paid';
    return { line, cls: 'seen', title: `Paid in full ${euro(Number(b.total) || 0)}`, sub, pill: { tone: 'ok', word: 'Done' }, proof: whole.proof, openByDefault: false };
  }
  const title = `${line.kind === 'deposit' ? 'Deposit' : 'Balance'} ${euro(line.kind === 'deposit' ? line.asked : Math.max(line.asked, line.left))}`;
  switch (line.state) {
    case 'paid': {
      const proof = line.proof;
      const linked = proof ? (b.payments ?? []).find((p) => p.proofId === proof.id) : undefined;
      // Payments arrive newest first: the one that completed the line is the first (PR #255 review, #35/#36).
      const desk = (b.payments ?? []).filter((p) => p.kind === line.kind && !p.voidedAt).at(0);
      // THE LINE UNDER THE NAME, SHORT (Freddi, 2026-10-13: "apply the same logic to subtitles in moneys
      // card too"): the state and its day, as the Vaccinations card says it.
      const sub = linked ? `Paid · ${day(linked.receivedOn)}` : desk ? `Paid at the desk · ${day(desk.receivedOn)}` : 'Paid';
      return { line, cls: 'seen', title, sub, pill: { tone: 'ok', word: 'Done' }, proof, openByDefault: false };
    }
    case 'pending': {
      const proof = line.proof!;
      // Whoever sent it (PR #260 review): the household's person with that sign-in, else the first contact.
      const who = proof.uploadedRole === 'desk' ? 'Patricia' : nameOf?.(proof.uploadedBy) ?? first;
      return { line, cls: 'read', title, sub: `From ${who} · ${day(proof.createdAt)}`, pill: { tone: 'warn', word: 'To check' }, proof, openByDefault: isLead };
    }
    case 'refused': {
      const proof = line.proof!;
      return { line, cls: 'no', title, sub: `Not right · ${day(proof.rejectedAt ?? proof.createdAt)}`, pill: { tone: 'bad', word: 'Not right' }, proof, openByDefault: isLead };
    }
    default: {
      const sub = line.paid > 0 ? `${euro(line.left)} still to come` : 'No screenshot yet';
      return { line, cls: 'mute', title, sub, pill: { tone: 'todo', word: 'Due' }, proof: null, openByDefault: isLead };
    }
  }
}

/**
 * THE MONEY'S WORDS for the slot under the band — one shape for every state
 * (messages-one-shape): the fact, the sign-up link for a person with no
 * account (`link`, or the placeholder until it is made), the ask, the sign-off.
 */
export function moneyMessage(
  s: MoneyState, b: BookingDetail, first: string, link: string | null = null,
  ctx: { today?: string; nameOf?: (email: string) => string | null; sentByRecipient?: (email: string) => boolean } = {},
): string {
  const who = dogNames(b);
  const are = (b.dogs ?? []).length === 1 ? 'is' : 'are';
  const when = dates(b);
  // "SEE YOU ON" ONLY FOR A BOOKED STAY STILL TO COME (audit, 2026-10-15): not once it has begun or is over.
  const toCome = (!ctx.today || b.checkIn > ctx.today) && !['requested', 'declined', 'cancelled', 'collected'].includes(b.status);
  // And once it is not, the link line offers stays to book, not "the booking" (PR #275 review).
  const linkLead = toCome ? LINK_LEAD : LINK_LEAD_STAYS;
  if (s.settled) return shapeMessage({ first, fact: `${who}, ${when} is paid in full`, ask: toCome ? `See you on ${stayDay(b.checkIn)}.` : null, link, linkLead });
  const { lead } = s;
  if (lead.state === 'pending') {
    // NOT "YOUR" SCREENSHOT WHEN SOMEONE ELSE SENT IT (audit, 2026-10-15): the sender with no phone, so the
    // words go to another of the household.
    // Who they are, not their first name (PR #275 review): two of a household can share one.
    const email = lead.proof?.uploadedRole === 'customer' ? lead.proof.uploadedBy : null;
    const by = email ? ctx.nameOf?.(email) ?? null : null;
    const mine = !email || (ctx.sentByRecipient ? ctx.sentByRecipient(email) : (by ?? first) === first);
    const fact = mine ? 'got your screenshot' : by && by !== first ? `got ${by}’s screenshot` : 'got the screenshot';
    return shapeMessage({ first, fact, ask: 'I’ll check it against the bank and confirm.', link, linkLead });
  }
  if (lead.state === 'refused') return withLinkLine(lead.proof?.rejectedReason ?? moneyNotRightLine(first, lead.kind), link, linkLead);
  // No balance line (Freddi, 2026-10-12: "remove this ... The €355 balance is due at collection on 27 Dec").
  if (lead.kind === 'remainder') return shapeMessage({ first, fact: `many thanks for the ${euro(s.lines.deposit.asked)} deposit for ${who}, ${when}`, link, linkLead });
  if (lead.paid > 0) {
    return shapeMessage({ first, fact: `thanks — ${euro(lead.paid)} of the deposit is in for ${who}, ${when}`, ask: `${euro(lead.left)} more secures the booking — once it’s sent, upload the screenshot in the app.`, link, linkLead });
  }
  // THE AMOUNT IS IN THE ASK (Freddi, 2026-10-14): the deposit the record asks — the lesser of the
  // deposit minimum and the total — and a stay at or under the minimum is the whole of it, up front.
  const asked = s.lines.deposit.asked;
  const fact = asked < s.total
    ? `${who} ${are} booked in for ${when} — a ${euro(asked)} deposit secures it`
    : `${who} ${are} booked in for ${when} — it comes to ${euro(s.total)} all in, paid up front`;
  return shapeMessage({ first, fact, ask: PAY_ASK, link, linkLead });
}

/** The sign-in that sent what the Money card is about — a customer's screenshot waiting or refused — or null. */
export function moneySender(b: BookingDetail): string | null {
  const s = moneyState(b);
  // The lead's screenshot; else, once the deposit is in and the lead has moved on to the balance or the
  // stay is paid, the screenshot the thank-you is about (PR #267 review).
  const p = s.lead.proof ?? s.lines.deposit.proof ?? (b.proofs ?? []).find((x) => x.verifiedAt && x.uploadedRole === 'customer') ?? null;
  return p && p.uploadedRole === 'customer' ? p.uploadedBy : null;
}

/**
 * The slot's words, and — while the person's link is still to be made — the tap
 * that makes it. A state whose words carry no link line (registered, or the
 * paused card) needs nothing made.
 */
export function slotMessage(first: string, phone: string | null | undefined, words: (link: string | null) => string, link: string | null, issueLink?: () => Promise<string | null>) {
  const text = words(link);
  const pending = link === LINK_PENDING && text.includes(LINK_PENDING) && issueLink;
  return {
    first,
    phone,
    text,
    prepare: pending ? async () => { const url = await issueLink!(); return url ? words(url) : null; } : undefined,
  };
}

/** The amount draft belongs to one screenshot (PR #255 review, #19/#59): a new proof starts from its own left. */
export function draftAmount(typed: { proofId: string; value: string } | null, pending: { proofId: string; left: number } | null): string {
  if (!pending) return '';
  return typed && typed.proofId === pending.proofId ? typed.value : String(pending.left);
}

export function MoneyCardView({ booking, first, nameOf, sentByRecipient, today, busy, phone, link = null, issueLink, onAccept, onNotRight, onUpload, onRemove, open, onToggle, rowsOpen = false, confirmRemove = null, pendingVerdict = null, settling = false, na = null, status }: MoneyCardProps & { open: boolean; onToggle: () => void; na?: string | null; status?: string }) {
  const s = moneyState(booking);
  // A CANCELLED STAY IS READ-ONLY (AC9b; PR #255 review, #07/#31/#50): the
  // ledger as it was left — no verdicts, no drop zone, no remove, no ask.
  const closed = (status ?? booking.status) === 'cancelled';
  const raw = moneyBand(s, booking, first);
  const band = closed ? { ...raw, sentence: `Cancelled · ${raw.sentence}` } : raw;
  // The trail, newest first: the balance above the deposit once the deposit is
  // covered (AC9a), or while a balance screenshot is in play (PR #255 review, #21/#60) — and one row
  // when one payment paid the lot (trailLines).
  const trail = trailLines(s, booking);
  const rows = trail.lines.map((l) => rowOf(l, booking, first, !s.settled && l.kind === s.lead.kind, nameOf, trail.whole));
  // SCREENSHOTS LOAD ONCE THEY CAN BE SEEN (PR #255 review, #42): the trail is
  // mounted shut so it can slide, and a shut card fetched every image anyway.
  const [seen, setSeen] = useState(open);
  useEffect(() => { if (open) setSeen(true); }, [open]);
  const [rowSeen, setRowSeen] = useState<Partial<Record<MoneyLine['kind'], boolean>>>({});
  const [rowOpen, setRowOpen] = useState<Partial<Record<MoneyLine['kind'], boolean>>>({});
  // THE VERDICT'S MOTION (Freddi, 2026-10-14: "the accept button shows a loading spinner … but the card
  // should not collapse", then "from accepting to loading skeleton to finished state"). The button pressed
  // spins while the server works; while the booking re-reads, the decision is a skeleton; then the row is
  // whatever the booking now says. The row is pinned open at the press, so it never shuts under her.
  const [acting, setActing] = useState<{ proofId: string; verdict: Verdict } | null>(pendingVerdict);
  useEffect(() => { if (acting && !busy && !settling) setActing(null); }, [acting, busy, settling]);
  const decide = (kind: MoneyLine['kind'], proofId: string, verdict: Verdict, run: () => void) => {
    setRowOpen((cur) => ({ ...cur, [kind]: true }));
    setActing({ proofId, verdict });
    run();
  };
  const [removing, setRemoving] = useState<string | null>(confirmRemove);
  const [oldOpen, setOldOpen] = useState<Record<string, boolean>>({});
  // EVERY ATTEMPT STAYS ON THE TRAIL (vaccination-cards AC18): `moneyLines` keeps one screenshot
  // per line, so a refused one replaced by a newer one left the card. It stays, beneath its line,
  // read-only — the record of what was sent and said no to, newest first.
  const earlier = (line: MoneyLine) => (booking.proofs ?? [])
    .filter((p) => p.kind === line.kind && p.rejectedAt !== null && p.id !== line.proof?.id)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  const [full, setFull] = useState<PaymentProof | null>(null);
  // THE AMOUNT IS HERS TO CONFIRM (AC10a): prefilled with what is still due
  // on the line the screenshot is for, and editable before she accepts.
  const [typed, setTyped] = useState<{ proofId: string; value: string } | null>(null);
  const pending = s.lead.state === 'pending' ? s.lead : null;
  const amount = draftAmount(typed, pending?.proof ? { proofId: pending.proof.id, left: pending.left } : null);
  // A line paid by hand while the screenshot waited accepts at zero: the server links or stamps it (AC12a, AC12c; PR #255 review, #46).
  const acceptable = pending ? Number(amount) > 0 || (pending.left <= 0 && Number(amount) === 0) : false;
  const linkedPayment = (proofId: string) => (booking.payments ?? []).find((p) => p.proofId === proofId && !p.voidedAt) ?? null;
  // REMOVE IS AN × IN THE SHOT'S CORNER (Freddi, 2026-10-11: "in the top right in the
  // screenshot section … just appear as an x"); the confirm unfolds beneath the shot.
  const shot = (proof: PaymentProof, alt: string, muted = false, load = true) => (
    <>
      <Shot id={proof.id} mime={proof.mime} alt={alt} muted={muted} load={load}
        corner={!closed && <button type="button" className="mc-remove" aria-label="Remove this screenshot" title="Remove this screenshot" disabled={busy} onClick={() => setRemoving(proof.id)}><Icon name="close" size={12} /></button>} />
      {removing === proof.id && (
        <div className="mc-remove-ask">
          <span><b>Remove this screenshot?</b>{linkedPayment(proof.id) ? ` The ${euro(linkedPayment(proof.id)!.amount)} it recorded is undone, and the line waits for a screenshot again.` : ''}</span>
          <span className="mc-remove-acts">
            <button type="button" className="btn quiet-danger mc-remove-yes" disabled={busy} onClick={() => { setRemoving(null); onRemove(proof.id); }}>Remove</button>
            <button type="button" className="btn quiet mc-remove-no" onClick={() => setRemoving(null)}>Keep</button>
          </span>
        </div>
      )}
    </>
  );
  const dropZone = <DropZone title="Add a screenshot" sub="A screenshot or a PDF of the transfer" busy={busy} onFile={(f) => onUpload(f)} />;
  // NEVER TOO LATE FOR A SCREENSHOT (Freddi, 2026-10-13: "if we mark a booking as paid on the desk there
  // should still be an empty state for a screenshot even after the fact"): a line paid with none keeps
  // its upload area, and the screenshot is filed against that line.
  const lateDrop = (kind: 'deposit' | 'remainder') => <DropZone title="Add a screenshot" sub="For the record — the payment is already in" busy={busy} onFile={(f) => onUpload(f, kind)} />;

  if (na) return <MoneyCardNa why={na} />;
  return (
    <SelfServiceCard subject="money" kind="money ss-money" tone={s.tone} open={open} onToggle={onToggle} title="Money" band={band}
      message={closed ? null : slotMessage(first, phone, (l) => moneyMessage(s, status ? { ...booking, status: status as BookingDetail['status'] } : booking, first, l, { today, nameOf, sentByRecipient }), link, issueLink)}
      after={full && <ProofFull id={full.id} mime={full.mime} alt="The screenshot" onClose={() => setFull(null)} />}>
      {rows.flatMap((r) => {
        const isOpen = rowsOpen || (rowOpen[r.line.kind] ?? r.openByDefault);
        const load = seen && (isOpen || !!rowSeen[r.line.kind]);
        const alt = `The ${r.line.kind === 'deposit' ? 'deposit' : 'balance'} screenshot`;
        const history = earlier(r.line).map((p) => {
          const open = rowsOpen || !!oldOpen[p.id];
          return (
            <TrailRow key={`old-${p.id}`} cls="no" uploaded title="Earlier screenshot" sub={`Not right · ${day(p.rejectedAt!)}`}
              pill={{ tone: 'bad', word: 'Not right' }} open={open} verdict="bad"
              thumb={<TrailThumb proof={p} seen={seen} emptyTitle="" onFull={() => setFull(p)} />}
              onToggle={() => setOldOpen((cur) => ({ ...cur, [p.id]: !open }))}>
              <div className="big xl">
                <Shot id={p.id} mime={p.mime} alt="An earlier screenshot" muted load={seen && open} />
              </div>
            </TrailRow>
          );
        });
        return [(
          <TrailRow key={r.line.kind} cls={r.cls} uploaded={r.proof !== null} title={r.title} sub={r.sub} pill={r.pill} open={isOpen}
            verdict={r.line.state === 'refused' ? 'bad' : r.line.state === 'paid' ? 'ok' : null}
            thumb={<TrailThumb proof={r.proof} seen={seen} emptyTitle="Nothing uploaded yet" onFull={() => setFull(r.proof)} />}
            onToggle={() => { setRowSeen((cur) => ({ ...cur, [r.line.kind]: true })); setRowOpen((cur) => ({ ...cur, [r.line.kind]: !isOpen })); }}>
            {/* NO LINE BENEATH THE AREA, IN ANY STATE (Freddi, 2026-10-13: "ensure the same structure and
                laws apply to both cards like by removing the received on x date from under the attached
                screenshot"): one area — the screenshot with its ×, or the drop zone — and only the
                decision under a screenshot waiting on her. The row's line says the rest. */}
            {r.line.state === 'pending' && r.proof && closed && (
              <div className="big xl">{shot(r.proof, alt, false, load)}</div>
            )}
            {r.line.state === 'pending' && r.proof && !closed && (
              <div className="big xl">
                {shot(r.proof, alt, false, load)}
                {acting?.proofId === r.proof.id && !busy && settling ? <DecideSkeleton /> : <DecideBlock label="Amount" note="What the transfer shows — change it for a part payment or the whole stay." acceptable={acceptable} busy={busy || acting !== null} rejectLabel="Not right"
                  pending={acting?.proofId === r.proof.id ? acting.verdict : null}
                  input={<input aria-label="Amount" inputMode="decimal" value={amount} onChange={(e) => setTyped({ proofId: r.proof!.id, value: e.target.value })} />}
                  onAccept={() => decide(r.line.kind, r.proof!.id, 'accept', () => onAccept(r.proof!.id, Number(amount)))}
                  onReject={() => decide(r.line.kind, r.proof!.id, 'reject', () => onNotRight(r.proof!.id))} />}
              </div>
            )}
            {r.line.state === 'refused' && r.proof && (
              <div className="big xl">{shot(r.proof, alt, true, load)}</div>
            )}
            {r.line.state === 'paid' && r.proof && (
              <div className="big xl">{shot(r.proof, alt, false, load)}</div>
            )}
            {/* On the one Paid in full row the screenshot files against the payment that paid it — the desk tags its own as balance. */}
            {r.line.state === 'paid' && !r.proof && !closed && lateDrop(trail.whole?.payment?.kind ?? r.line.kind)}
            {r.line.state === 'due' && !closed && dropZone}
          </TrailRow>
        ), ...history];
      })}
    </SelfServiceCard>
  );
}
